'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const PHONE_HREF = 'tel:09844068955';

  useEffect(() => {
    const run = async () => {
      const { gsap } = await import('gsap');

      const tl = gsap.timeline({ delay: 0.2 });

      const words = headlineRef.current?.querySelectorAll('.hero-word');
      if (words) {
        tl.fromTo(
          words,
          { y: '110%', opacity: 0 },
          {
            y: '0%',
            opacity: 1,
            duration: 0.9,
            ease: 'power4.out',
            stagger: 0.06
          }
        );
      }

      tl.fromTo(
        subRef.current,
        { opacity: 0, y: 20, filter: 'blur(4px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, ease: 'power3.out' },
        '-=0.4'
      );
    };

    run();
  }, []);

  const headline = 'Transform Your Office Into a High-Performance Workspace';
  const words = headline.split(' ');

  return (
    <section data-component-id="src/app/components/HeroSection.tsx:49:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="49" data-component-end-line="98" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20min-h-screen%20flex%20flex-col%20justify-center%20overflow-hidden%20bg-white%20pt-24%20pb-16%22%7D" className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white pt-24 pb-16">

      <div data-component-id="src/app/components/HeroSection.tsx:51:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="51" data-component-end-line="51" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20grid-bg%20pointer-events-none%22%7D" className="absolute inset-0 grid-bg pointer-events-none" />

      <div data-component-id="src/app/components/HeroSection.tsx:53:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="53" data-component-end-line="97" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20max-w-7xl%20mx-auto%20w-full%20px-6%22%7D" className="relative z-10 max-w-7xl mx-auto w-full px-6">
        <div data-component-id="src/app/components/HeroSection.tsx:54:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="54" data-component-end-line="96" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20lg%3Agrid-cols-12%20gap-12%20items-center%22%7D" className="grid lg:grid-cols-12 gap-12 items-center">

          {/* LEFT */}
          <div data-component-id="src/app/components/HeroSection.tsx:57:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="57" data-component-end-line="89" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-7%20flex%20flex-col%20gap-8%22%7D" className="lg:col-span-7 flex flex-col gap-8">

            <div data-component-id="src/app/components/HeroSection.tsx:59:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="59" data-component-end-line="64" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20bg-primary%2F5%20border%20border-primary%2F10%20rounded-full%20px-4%20py-2%20w-fit%22%7D" className="inline-flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-full px-4 py-2 w-fit">
              <span data-component-id="src/app/components/HeroSection.tsx:60:14" data-component-path="src/app/components/HeroSection.tsx" data-component-line="60" data-component-end-line="60" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-2%20h-2%20rounded-full%20bg-accent%20animate-pulse%22%7D" className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span data-component-id="src/app/components/HeroSection.tsx:61:14" data-component-path="src/app/components/HeroSection.tsx" data-component-line="61" data-component-end-line="63" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-semibold%20tracking-widest%20uppercase%20text-primary%22%2C%22textContent%22%3A%22Bangalore's%20Premier%20Office%20Interior%20Company%22%7D" className="text-xs font-semibold tracking-widest uppercase text-primary">
                Bangalore&apos;s Premier Office Interior Company
              </span>
            </div>

            <div data-component-id="src/app/components/HeroSection.tsx:66:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="66" data-component-end-line="74" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-hidden%22%7D" ref={headlineRef} className="overflow-hidden">
              <h1 data-component-id="src/app/components/HeroSection.tsx:67:14" data-component-path="src/app/components/HeroSection.tsx" data-component-line="67" data-component-end-line="73" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20sm%3Atext-5xl%20lg%3Atext-%5B3rem%5D%20font-extrabold%20text-primary%20leading-%5B1.08%5D%20tracking-tight%20flex%20flex-wrap%20gap-x-%5B0.28em%5D%22%7D" className="text-4xl sm:text-5xl lg:text-[3rem] font-extrabold text-primary leading-[1.08] tracking-tight flex flex-wrap gap-x-[0.28em]">
                {words.map((word, i) => (
                  <span data-component-id="src/app/components/HeroSection.tsx:69:18" data-component-path="src/app/components/HeroSection.tsx" data-component-line="69" data-component-end-line="71" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22overflow-hidden%20inline-block%22%7D" key={i} className="overflow-hidden inline-block">
                    <span data-component-id="src/app/components/HeroSection.tsx:70:20" data-component-path="src/app/components/HeroSection.tsx" data-component-line="70" data-component-end-line="70" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hero-word%20inline-block%22%7D" className="hero-word inline-block">{word}</span>
                  </span>
                ))}
              </h1>
            </div>

            <p data-component-id="src/app/components/HeroSection.tsx:76:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="76" data-component-end-line="82" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20md%3Atext-xl%20text-muted-foreground%20leading-relaxed%20max-w-xl%20font-medium%22%2C%22textContent%22%3A%22Expert%20Corporate%20Interior%20Design%20%26%20Turnkey%20Office%20Solutions%20in%20Bangalore.%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20From%20concept%20to%20completion%20%E2%80%94%20delivered%20on%20time%2C%20every%20time.%22%7D"
              ref={subRef}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl font-medium"
            >
              Expert Corporate Interior Design & Turnkey Office Solutions in Bangalore.
              From concept to completion — delivered on time, every time.
            </p>

            <a data-component-id="src/app/components/HeroSection.tsx:84:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="84" data-component-end-line="87" data-component-file="HeroSection.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%5Bvar%3APHONE_HREF%5D%22%2C%22className%22%3A%22btn-primary%20text-base%20px-10%20py-4%20w-fit%20inline-flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22098440%2068955%22%7D" href={PHONE_HREF} className="btn-primary text-base px-10 py-4 w-fit inline-flex items-center gap-2">
              <Icon data-component-id="src/app/components/HeroSection.tsx:85:14" data-component-path="src/app/components/HeroSection.tsx" data-component-line="85" data-component-end-line="85" data-component-file="HeroSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PhoneIcon%22%7D" name="PhoneIcon" size={18} variant="solid" />
              098440 68955
            </a>

          </div>

          {/* RIGHT */}
          <div data-component-id="src/app/components/HeroSection.tsx:92:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="92" data-component-end-line="94" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-5%22%7D" className="lg:col-span-5">
            <MultiStepForm data-component-id="src/app/components/HeroSection.tsx:93:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="93" data-component-end-line="93" data-component-file="HeroSection.tsx" data-component-name="MultiStepForm" data-component-content="%7B%22elementName%22%3A%22MultiStepForm%22%7D" />
          </div>

        </div>
      </div>
    </section>
  );
}

/* =========================
   MULTI STEP FORM
========================= */

function MultiStepForm() {
  const [step, setStep] = React.useState(1);

  const [form, setForm] = React.useState({
    name: '',
    phone: '',
    email: '',
    officeSize: '',
    requirement: '',
    message: ''
  });

  const [errors, setErrors] = React.useState<any>({});

  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const run = async () => {
      const { gsap } = await import('gsap');

      if (!formRef.current) return;

      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 40, filter: 'blur(6px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'power4.out'
        }
      );
    };

    run();
  }, []);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /* -------------------------
     VALIDATION
  --------------------------*/
  const validateStep1 = () => {
    const newErrors: any = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!form.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10}$/.test(form.phone)) {
      newErrors.phone = 'Enter valid 10-digit number';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* -------------------------
     SUBMIT
  --------------------------*/
  const handleSubmit = async () => {
    try {
      await fetch('https://script.google.com/macros/s/AKfycbxvNnVX4aCK8_EUltWN90S7EXxYFrc7X9ouLWPE1FwvceKXQevkNO2rUx6BfKFRwSvY/exec', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          timestamp: new Date().toISOString()
        })
      });

      alert('Submitted successfully!');

      setForm({
        name: '',
        phone: '',
        email: '',
        officeSize: '',
        requirement: '',
        message: ''
      });

      setStep(1);
    } catch (err) {
      alert('Submission failed');
    }
  };

  return (
    <div data-component-id="src/app/components/HeroSection.tsx:205:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="205" data-component-end-line="322" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22form%22%2C%22className%22%3A%22bg-white%20border%20border-border%20shadow-xl%20rounded-2xl%20p-6%20md%3Ap-8%22%7D"
      id="form"
      ref={formRef}
      className="bg-white border border-border shadow-xl rounded-2xl p-6 md:p-8"
    >
      <h3 data-component-id="src/app/components/HeroSection.tsx:210:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="210" data-component-end-line="212" data-component-file="HeroSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-bold%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Free%20Office%20Design%20Consultation%22%7D" className="text-xl font-bold text-primary mb-4">
        Free Office Design Consultation
      </h3>

      {/* PROGRESS */}
      <div data-component-id="src/app/components/HeroSection.tsx:215:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="215" data-component-end-line="224" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%20mb-6%22%7D" className="flex gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div data-component-id="src/app/components/HeroSection.tsx:217:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="217" data-component-end-line="222" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            key={s}
            className={`h-1 flex-1 rounded-full ${
              step >= s ? 'bg-accent' : 'bg-primary/10'
            }`}
          />
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div data-component-id="src/app/components/HeroSection.tsx:228:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="228" data-component-end-line="261" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          <Input data-component-id="src/app/components/HeroSection.tsx:229:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="229" data-component-end-line="234" data-component-file="HeroSection.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%7D"
            label="Full Name"
            value={form.name}
            onChange={(v) => handleChange('name', v)}
            error={errors.name}
          />

          <Input data-component-id="src/app/components/HeroSection.tsx:236:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="236" data-component-end-line="244" data-component-file="HeroSection.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%2C%22type%22%3A%22tel%22%7D"
            label="Phone Number"
            value={form.phone}
            onChange={(v) =>
              handleChange('phone', v.replace(/[^0-9]/g, ''))
            }
            error={errors.phone}
            type="tel"
          />

          <Input data-component-id="src/app/components/HeroSection.tsx:246:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="246" data-component-end-line="251" data-component-file="HeroSection.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%7D"
            label="Email Address"
            value={form.email}
            onChange={(v) => handleChange('email', v)}
            error={errors.email}
          />

          <button data-component-id="src/app/components/HeroSection.tsx:253:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="253" data-component-end-line="260" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22btn-primary%20w-full%22%2C%22textContent%22%3A%22Next%22%7D"
            onClick={() => {
              if (validateStep1()) setStep(2);
            }}
            className="btn-primary w-full"
          >
            Next
          </button>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div data-component-id="src/app/components/HeroSection.tsx:266:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="266" data-component-end-line="293" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          <Select data-component-id="src/app/components/HeroSection.tsx:267:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="267" data-component-end-line="272" data-component-file="HeroSection.tsx" data-component-name="Select" data-component-content="%7B%22elementName%22%3A%22Select%22%7D"
            label="Office Size"
            value={form.officeSize}
            onChange={(v) => handleChange('officeSize', v)}
            options={['1000 sq ft', '1000–3000 sq ft', '3000+ sq ft']}
          />

          <Select data-component-id="src/app/components/HeroSection.tsx:274:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="274" data-component-end-line="283" data-component-file="HeroSection.tsx" data-component-name="Select" data-component-content="%7B%22elementName%22%3A%22Select%22%7D"
            label="Requirement Type"
            value={form.requirement}
            onChange={(v) => handleChange('requirement', v)}
            options={[
              'New Office Setup',
              'Office Renovation',
              'Turnkey Fit-Out'
            ]}
          />

          <div data-component-id="src/app/components/HeroSection.tsx:285:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="285" data-component-end-line="292" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%22%7D" className="flex gap-3">
            <button data-component-id="src/app/components/HeroSection.tsx:286:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="286" data-component-end-line="288" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-1%2F2%20border%20py-3%20rounded-lg%22%2C%22textContent%22%3A%22Back%22%7D" onClick={() => setStep(1)} className="w-1/2 border py-3 rounded-lg">
              Back
            </button>
            <button data-component-id="src/app/components/HeroSection.tsx:289:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="289" data-component-end-line="291" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22btn-primary%20w-1%2F2%22%2C%22textContent%22%3A%22Next%22%7D" onClick={() => setStep(3)} className="btn-primary w-1/2">
              Next
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div data-component-id="src/app/components/HeroSection.tsx:298:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="298" data-component-end-line="320" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          <textarea data-component-id="src/app/components/HeroSection.tsx:299:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="299" data-component-end-line="305" data-component-file="HeroSection.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22className%22%3A%22w-full%20border%20border-border%20rounded-lg%20px-4%20py-3%20focus%3Aring-2%20focus%3Aring-accent%20outline-none%22%7D"
            rows={5}
            value={form.message}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="Tell us about your office requirement..."
            className="w-full border border-border rounded-lg px-4 py-3 focus:ring-2 focus:ring-accent outline-none"
          />

          <div data-component-id="src/app/components/HeroSection.tsx:307:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="307" data-component-end-line="315" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%22%7D" className="flex gap-3">
            <button data-component-id="src/app/components/HeroSection.tsx:308:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="308" data-component-end-line="310" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-1%2F2%20border%20py-3%20rounded-lg%22%2C%22textContent%22%3A%22Back%22%7D" onClick={() => setStep(2)} className="w-1/2 border py-3 rounded-lg">
              Back
            </button>

            <button data-component-id="src/app/components/HeroSection.tsx:312:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="312" data-component-end-line="314" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22btn-primary%20w-1%2F2%22%2C%22textContent%22%3A%22Submit%22%7D" onClick={handleSubmit} className="btn-primary w-1/2">
              Submit
            </button>
          </div>

          <p data-component-id="src/app/components/HeroSection.tsx:317:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="317" data-component-end-line="319" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20text-center%22%2C%22textContent%22%3A%22We%E2%80%99ll%20respond%20within%2024%20hours%22%7D" className="text-xs text-muted-foreground text-center">
            We’ll respond within 24 hours
          </p>
        </div>
      )}
    </div>
  );
}

/* =========================
   INPUT
========================= */

function Input({
  label,
  value,
  onChange,
  error,
  type = 'text'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  type?: string;
}) {
  return (
    <div data-component-id="src/app/components/HeroSection.tsx:344:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="344" data-component-end-line="355" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
      <label data-component-id="src/app/components/HeroSection.tsx:345:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="345" data-component-end-line="345" data-component-file="HeroSection.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22text-sm%20font-medium%20text-muted-foreground%22%7D" className="text-sm font-medium text-muted-foreground">{label}</label>

      <input data-component-id="src/app/components/HeroSection.tsx:347:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="347" data-component-end-line="352" data-component-file="HeroSection.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22className%22%3A%22mt-1%20w-full%20rounded-lg%20border%20border-border%20px-4%20py-3%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent%22%7D"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent"
      />

      {error && <p data-component-id="src/app/components/HeroSection.tsx:354:16" data-component-path="src/app/components/HeroSection.tsx" data-component-line="354" data-component-end-line="354" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-red-500%20mt-1%22%7D" className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

/* =========================
   SELECT
========================= */

function Select({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div data-component-id="src/app/components/HeroSection.tsx:375:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="375" data-component-end-line="388" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
      <label data-component-id="src/app/components/HeroSection.tsx:376:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="376" data-component-end-line="376" data-component-file="HeroSection.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22text-sm%20font-medium%20text-muted-foreground%22%7D" className="text-sm font-medium text-muted-foreground">{label}</label>

      <select data-component-id="src/app/components/HeroSection.tsx:378:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="378" data-component-end-line="387" data-component-file="HeroSection.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22className%22%3A%22mt-1%20w-full%20rounded-lg%20border%20border-border%20px-4%20py-3%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent%22%7D"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <option data-component-id="src/app/components/HeroSection.tsx:383:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="383" data-component-end-line="383" data-component-file="HeroSection.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22textContent%22%3A%22Select%22%7D" value="">Select</option>
        {options.map((opt) => (
          <option data-component-id="src/app/components/HeroSection.tsx:385:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="385" data-component-end-line="385" data-component-file="HeroSection.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%7D" key={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}